import { useCallback, useEffect, useRef, useState } from 'react';
import { ExtractError, type ExtractErrorCode, extractPalette } from './lib/extract';
import { isAchromatic } from './lib/harmony';
import { Uploader } from './components/Uploader';
import { PaletteGrid } from './components/PaletteGrid';
import { HarmonyPanel } from './components/HarmonyPanel';
import { ExportTabs } from './components/ExportTabs';
import styles from './styles/App.module.css';

const ERROR_MESSAGES: Record<ExtractErrorCode, string> = {
  'not-an-image': "That file isn't an image. Try a PNG, JPG, GIF, or WebP.",
  'too-large': 'That image is over 25 MB. Try a smaller file.',
  'undecodable': "We couldn't read that image. It may be corrupt or an unsupported format.",
  'image-empty-or-transparent': "That image had no visible colors to sample.",
};

export function App() {
  const [colors, setColors] = useState<string[]>([]);
  const [baseColor, setBaseColor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A blob: URL of the loaded image, shown as a thumbnail. Held in a ref too so
  // we can revoke the previous one without it being a render dependency.
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    [],
  );

  // Bumped per file so a slow extraction can't overwrite a newer one's result.
  const requestId = useRef(0);

  const handleFile = useCallback(async (file: File) => {
    const id = ++requestId.current;
    setBusy(true);
    setError(null);
    try {
      const palette = await extractPalette(file);
      if (id !== requestId.current) return;
      // Swap the preview only once we know the file decoded, revoking the URL
      // the previous image held. A rejected file leaves the last result up.
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const url = URL.createObjectURL(file);
      previewUrlRef.current = url;
      setPreview({ url, name: file.name });
      setColors(palette);
      // Default base: the most prominent colour that actually has a hue, so
      // harmonies aren't built on a white or dark background.
      setBaseColor(palette.find((c) => !isAchromatic(c)) ?? palette[0] ?? null);
    } catch (err) {
      if (id !== requestId.current) return;
      const code = err instanceof ExtractError ? err.code : null;
      setError(code ? ERROR_MESSAGES[code] : 'Something went wrong reading that image.');
    } finally {
      if (id === requestId.current) setBusy(false);
    }
  }, []);

  return (
    <main className={styles.app}>
      <Uploader onFile={handleFile} busy={busy} error={error} preview={preview} />

      {colors.length > 0 && (
        <div className={styles.results}>
          <div className={styles.column}>
            <PaletteGrid colors={colors} baseColor={baseColor} onSelectBase={setBaseColor} />
            <HarmonyPanel baseColor={baseColor} />
          </div>
          <div className={`${styles.column} ${styles.exportColumn}`}>
            <ExportTabs colors={colors} baseColor={baseColor} />
          </div>
        </div>
      )}
    </main>
  );
}
